import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Calendar, User, ArrowRight, ArrowLeft, Loader2, Lock, Tag, Crown } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../i18n/LanguageContext';
import { usePageMeta } from '../lib/seo';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import LockedDialog, { hasAccess } from '../components/LockedDialog';
import { useCategories } from '../hooks/useCategories';
import { isHtml, safeHtml, stripHtml } from '../lib/richText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absUrl = (u) => (u && u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u);

const isVideoUrl = (u) => {
  if (!u || typeof u !== 'string') return false;
  if (u.includes('/video/upload/')) return true; // Cloudinary video
  if (u.includes('/api/uploads/videos/')) return true; // Local uploads
  if (/\.(mp4|mov|webm|m4v|ogg)(\?|$)/i.test(u)) return true;
  return false;
};

const normalize = (x) => ({
  id: x.id,
  title: { ar: x.titleAr || x.title?.ar, en: x.titleEn || x.title?.en },
  excerpt: { ar: x.excerptAr || x.contentAr || x.excerpt?.ar, en: x.excerptEn || x.contentEn || x.excerpt?.en },
  content: { ar: x.contentAr || x.content?.ar || x.excerptAr, en: x.contentEn || x.content?.en || x.excerptEn },
  category: x.category || 'general',
  cover: x.cover ? (typeof x.cover === 'string' && x.cover.startsWith('/api/') ? `${BACKEND_URL}${x.cover}` : x.cover) : 'https://images.pexels.com/photos/8438967/pexels-photo-8438967.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200',
  date: typeof x.date === 'string' ? x.date : (x.created_at || '').slice(0, 10),
  author: typeof x.author === 'object' ? x.author : { ar: x.author_name || 'فريق Automeet Network', en: x.author_name || 'Automeet Network Team' },
  is_free: !!x.is_free,
  sections: Array.isArray(x.sections) ? [...x.sections].sort((a, b) => (a.order || 0) - (b.order || 0)) : [],
});

const PostDetail = () => {
  const { id } = useParams();
  const { lang, isRTL, t } = useLang();
  const { user } = useAuth();
  const { getLabel: getCategoryLabel } = useCategories();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  usePageMeta(post && pickLocalized(post.title, lang), post && stripHtml(pickLocalized(post.excerpt || post.content, lang)));
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const Arrow = isRTL ? ArrowRight : ArrowLeft;
  const canAccess = hasAccess(user) || !!post?.is_free;

  useEffect(() => {
    setShowLock(!!post && !canAccess);
  }, [canAccess, post]);

  useEffect(() => {
    const key = `viewed_post_${id}`;
    if (/^p\d+$/.test(id) || sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    api.post(`/posts/${id}/view`).catch(() => {});
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/posts');
        const found = (data || []).map(normalize).find(p => p.id === id);
        if (!cancelled) found ? setPost(found) : setNotFound(true);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="pt-28 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </main>
    );
  }

  if (notFound) return <Navigate to="/library" replace />;

  return (
    <>
      <main className="page-y min-h-screen" data-testid="post-detail-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 text-sm"
            data-testid="back-to-library"
          >
            <Arrow className="w-4 h-4" /> {tx(lang, 'العودة إلى المكتبة', 'Back to library')}
          </button>

          {/* Lock overlay state for non-subscribers - cover + title visible, content blurred via backdrop dialog */}
          {!canAccess && (
            <div className="mb-6 rounded-2xl bg-amber-500/10 border-2 border-amber-500/40 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-slate-900" />
              </div>
              <div className="flex-1">
                <div className="text-amber-700 font-bold mb-0.5">{tx(lang, 'محتوى حصري للمشتركين', 'Subscribers only content')}</div>
                <div className="text-slate-600 text-sm">{tx(lang, 'اشترك لقراءة المقال كاملاً', 'Subscribe to read the full article')}</div>
              </div>
              <Link to="/subscription" data-testid="post-banner-subscribe" className="btn-subscribe h-10 px-4 text-sm">
                <Crown className="w-4 h-4" />{tx(lang, 'اشترك الآن', 'Subscribe')}
              </Link>
            </div>
          )}

          {/* Hero */}
          <article>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 leading-tight">
              {pickLocalized(post.title, lang)}
            </h1>
            <div className={`flex items-center gap-4 text-sm text-slate-500 mb-6 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-1.5"><User className="w-4 h-4" />{pickLocalized(post.author, lang)}</div>
              <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /><span dir="ltr">{post.date}</span></div>
              {post.category && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold">
                  <Tag className="w-3 h-3" /> {getCategoryLabel(post.category, lang)}
                </span>
              )}
            </div>

            <div className="rounded-2xl overflow-hidden mb-8 bg-black border border-slate-200">
              {isVideoUrl(post.cover) ? (
                <video
                  src={post.cover}
                  controls
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  onContextMenu={(e) => e.preventDefault()}
                  preload="metadata"
                  className="w-full max-h-[500px]"
                />
              ) : (
                <img src={post.cover} alt="" className="w-full max-h-[500px] object-cover" />
              )}
            </div>

            <div className={`prose prose-invert max-w-none ${isRTL ? 'text-right' : 'text-left'} ${!canAccess ? 'select-none' : ''}`} style={!canAccess ? { filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none' } : {}}>
              {(() => {
                const body = pickLocalized(post.content || post.excerpt, lang);
                return isHtml(body)
                  ? <div data-testid="post-body-html" className="rich-content article-body text-slate-700 text-base md:text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: safeHtml(body) }} />
                  : <p data-testid="post-body-text" className="text-slate-700 text-base md:text-lg leading-relaxed whitespace-pre-line">{body}</p>;
              })()}

              {/* Ordered content sections */}
              {post.sections && post.sections.length > 0 && (
                <div className="mt-8 space-y-6" data-testid="post-sections">
                  {post.sections.map((s) => {
                    if (s.type === 'text') {
                      const text = lang === 'ar' ? (s.textAr || s.textEn) : (s.textEn || s.textAr);
                      if (!text) return null;
                      return (
                        <p key={s.id} className="text-slate-700 text-base md:text-lg leading-relaxed whitespace-pre-line m-0">
                          {text}
                        </p>
                      );
                    }
                    const cap = lang === 'ar' ? (s.captionAr || s.captionEn) : (s.captionEn || s.captionAr);
                    if (s.type === 'image' && s.url) {
                      return (
                        <figure key={s.id} className="m-0">
                          <div className="rounded-2xl overflow-hidden bg-black border border-slate-200">
                            <img src={absUrl(s.url)} alt={cap || ''} loading="lazy" className="w-full max-h-[600px] object-cover" />
                          </div>
                          {cap && (
                            <figcaption className={`text-slate-600 text-sm mt-2 px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {cap}
                            </figcaption>
                          )}
                        </figure>
                      );
                    }
                    if (s.type === 'video' && s.url) {
                      return (
                        <figure key={s.id} className="m-0">
                          <div className="rounded-2xl overflow-hidden bg-black border border-slate-200 aspect-video">
                            <video
                              src={absUrl(s.url)}
                              controls
                              controlsList="nodownload noplaybackrate noremoteplayback"
                              disablePictureInPicture
                              disableRemotePlayback
                              onContextMenu={(e) => e.preventDefault()}
                              preload="metadata"
                              className="w-full h-full"
                            />
                          </div>
                          {cap && (
                            <figcaption className={`text-slate-600 text-sm mt-2 px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {cap}
                            </figcaption>
                          )}
                        </figure>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>

            {!canAccess && (
              <div className="text-center mt-8">
                <Link to="/subscription" data-testid="post-bottom-subscribe" className="btn-subscribe h-12 px-6">
                  <Lock className="w-4 h-4" />
                  {tx(lang, 'اشترك لقراءة المقال كاملاً', 'Subscribe to read full article')}
                </Link>
              </div>
            )}
          </article>
        </div>
      </main>

      <LockedDialog open={showLock} onClose={() => setShowLock(false)} />
    </>
  );
};

export default PostDetail;
